import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils.ts';
import { Button } from './button.tsx';
import { Input } from './input.tsx';

export type SearchableSelectProps = {
  value?: string;
  onValueChange: (value: string) => void;
  /** Loads the options for a search term ("" on open). */
  loadOptions: (search: string) => Promise<string[]>;
  placeholder?: string;
  searchPlaceholder?: string;
  allLabel?: string;
  className?: string;
};

/** A button that opens a searchable, async-loaded option list with an "All" entry. */
export function SearchableSelect({
  value,
  onValueChange,
  loadOptions,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  allLabel = 'All',
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [options, setOptions] = React.useState<string[] | null>(null);
  const [position, setPosition] = React.useState({ top: 0, left: 0, width: 0 });
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 224),
      });
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      setOptions(null);
      setSearch('');
      return;
    }
    setOptions(null);
    let cancelled = false;
    const timer = setTimeout(
      async () => {
        try {
          const results = await loadOptions(search);
          if (!cancelled) setOptions(results);
        } catch {
          if (!cancelled) setOptions([]);
        }
      },
      search ? 300 : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, open, loadOptions]);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const t = event.target as Node;
      if (!buttonRef.current?.contains(t) && !dropdownRef.current?.contains(t)) setOpen(false);
    };
    const onScroll = (event: Event) => {
      if (dropdownRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const handleSelect = (selected: string) => {
    onValueChange(selected);
    setOpen(false);
    setSearch('');
  };

  const displayValue = value === 'all' ? allLabel : value || placeholder;
  const row = (selected: boolean) =>
    cn('flex cursor-pointer items-center px-3 py-2 text-sm hover:bg-slate-100', selected && 'bg-slate-100');
  const tick = (selected: boolean) => cn('mr-2 h-4 w-4', selected ? 'opacity-100' : 'opacity-0');

  const dropdown = open
    ? createPortal(
        <div
          ref={dropdownRef}
          role="listbox"
          className="fixed rounded-md border bg-card-bg shadow-lg"
          style={{ top: position.top, left: position.left, width: position.width, zIndex: 9999 }}
        >
          <div className="p-2">
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8"
              autoFocus
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            <div
              role="option"
              aria-selected={value === 'all'}
              className={row(value === 'all')}
              onClick={() => handleSelect('all')}
            >
              <Check className={tick(value === 'all')} />
              {allLabel}
            </div>
            {options === null && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              </div>
            )}
            {options !== null && options.length === 0 && (
              <div className="py-4 text-center text-sm text-slate-500">
                {search ? 'No results found' : 'No options available'}
              </div>
            )}
            {options?.map((option) => (
              <div
                key={option}
                role="option"
                aria-selected={value === option}
                className={row(value === option)}
                onClick={() => handleSelect(option)}
              >
                <Check className={tick(value === option)} />
                {option}
              </div>
            ))}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className={cn('relative', className)}>
      <Button
        ref={buttonRef}
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-36 justify-between font-normal"
        onClick={() => setOpen(!open)}
      >
        <span className="truncate">{displayValue}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {dropdown}
    </div>
  );
}
